import { useCallback } from 'react';
import { FilterIcon, CalendarIcon, MapPinIcon } from '@/shared/ui/Icons';
import { SearchableSelect } from '@/shared/components';
import type { SearchableOption } from '@/shared/components';
import { budgetsApi } from '../api';
import { regionApi } from '@/features/region/api';

interface BudgetFiltersBarProps {
  year: string;
  onYearChange: (value: string) => void;
  yearLabel?: string;
  governorateId: string;
  onGovernorateIdChange: (id: string, label: string) => void;
  governorateLabel?: string;
}

export function BudgetFiltersBar({
  year,
  onYearChange,
  yearLabel,
  governorateId,
  onGovernorateIdChange,
  governorateLabel,
}: BudgetFiltersBarProps) {
  const fetchYears = useCallback(async (search: string): Promise<SearchableOption[]> => {
    const data = await budgetsApi.getBudgetYears({
      skipCount: 0,
      maxResultCount: 100,
      year: search || undefined,
    });
    return (data ?? []).map((item) => ({
      id: String(item),
      label: String(item),
    }));
  }, []);

  const fetchGovernorates = useCallback(async (search: string): Promise<SearchableOption[]> => {
    const data = await regionApi.getGovernorates({
      filter: search || undefined,
    });
    return (data ?? []).map((item) => ({
      id: String(item.id),
      label: item.name,
    }));
  }, []);

  return (
    <div className="rounded-2xl border border-slate-200/60 bg-white px-5 py-4 shadow-soft">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-4">
        <div className="flex shrink-0 items-center gap-2 text-slate-600">
          <FilterIcon className="h-4 w-4" />
          <span className="text-sm font-medium">Filters</span>
        </div>
        <div className="h-6 w-px shrink-0 bg-slate-200" />
        <div className="flex shrink-0 items-center gap-2">
          <CalendarIcon className="h-4 w-4 shrink-0 text-slate-400" />
          <SearchableSelect
            value={year}
            onSelect={(id) => onYearChange(id)}
            fetchOptions={fetchYears}
            placeholder="Select year"
            selectedLabel={yearLabel ?? year}
            clearable
          />
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <MapPinIcon className="h-4 w-4 shrink-0 text-slate-400" />
          <SearchableSelect
            value={governorateId}
            onSelect={(id, label) => onGovernorateIdChange(id, label)}
            fetchOptions={fetchGovernorates}
            placeholder="Select governorate"
            selectedLabel={governorateLabel}
            clearable
          />
        </div>
      </div>
    </div>
  );
}
